import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel";
import { callApi, fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "@widgets/CommonDialog";

interface Props {
  Menuprops: menuInfo;
}

/**
 * 프로젝트 동적 컬럼의 field 접두사.
 *
 * pms_pjt_elemdist 는 프로젝트 × 예산코드 1행(세로)인데 화면은
 * 예산코드 1행 × 프로젝트 n열(가로)이라 조회할 때 피벗한다.
 * cd_pjt 를 그대로 컬럼키로 쓰면 고정 컬럼명(cd_budget 등)과 부딪힐 수 있어 접두어를 붙인다.
 *
 * 제품별배부(SN_COS_MAS_REG_007)와 같은 구조이고 피벗 축만 뒤집혀 있다
 * — 그쪽은 원가요소가 컬럼, 여기는 프로젝트가 컬럼이다.
 */
const PJT_PREFIX = "pjt_";

/** 합계금액 컬럼. 프로젝트 컬럼을 이 앞에 끼우고 자신은 항상 맨 뒤로 보낸다. */
const TOTAL_COLUMN = "am_dist";

/** 상단 버튼(sys_headerctrl)의 cd_param. 메타 행과 맞춰야 한다. */
const BTN_AGGREGATE = "btnAgg";

const SN_PMS_CLS_REG_003 = forwardRef(({ Menuprops }: Props, ref) => {
  const gridRef = useRef<SnGridRef>(null);
  const searchRef = useRef<any>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const baseColsRef = useRef<any[]>([]); // 정적 컬럼 원본(메타)
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any[]>([]);
  const [headerInfo, setHeaderInfo] = useState<any>({ controls: [], data: {} }); // 그리드 상단 버튼(sys_headerctrl)
  const [dataSource1, setDataSource1] = useState<any[]>([]);
  const { userInfo } = global();

  useEffect(() => {
    const fetchform = async () => {
      try {
        // 원가기간 초기값: 최신 마감월(공통 메타 쿼리)
        const _rtnYm: any = await fnGetDataSet({
          id: "com_CommonMapper_12",
          params: { cd_firm: userInfo.cd_firm, cd_biz: userInfo.cd_biz },
        });
        const _dt = Array.isArray(_rtnYm?.[0]) ? _rtnYm[0] : [];
        const newData = _dt?.[0]
          ? { dt_from: _dt[0].ym_start, dt_to: _dt[0].ym }
          : {};

        const rtn: any = await getInitFormData();
        baseColsRef.current = rtn.grid.grid1 ?? [];
        setFormData({ ...rtn.formData, ...newData });
        setFormControlData(rtn.formControlData);
        setColums(baseColsRef.current);
        setHeaderInfo(rtn.header?.grid1 ?? { controls: [], data: {} });
      } catch (error) {}
    };
    fetchform();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  //#region 프로젝트 동적 컬럼(피벗)
  // data[1](프로젝트 목록)으로 동적 컬럼을 만들어 정적 컬럼 뒤·합계금액 앞에 붙인다.
  const buildGridColumns = (pjtRows: any[]) => {
    const baseCols = baseColsRef.current ?? [];
    // 합계금액 컬럼은 항상 맨 뒤로 보내고, 프로젝트 컬럼 템플릿으로도 쓴다.
    const totalCol = baseCols.find((c: any) => c.cd_column === TOTAL_COLUMN);
    const staticCols = baseCols.filter(
      (c: any) => c.cd_column !== TOTAL_COLUMN
    );
    const template =
      totalCol ??
      baseCols.find((c: any) => c.tp_column === "SnNumberBox") ??
      baseCols[0] ??
      {};

    const maxSeq = staticCols.reduce(
      (m: number, c: any) => Math.max(m, Number(c.no_seq ?? 0)),
      0
    );

    const pjtCols = (pjtRows ?? []).map((p: any, i: number) => ({
      ...template,
      cd_column: PJT_PREFIX + p.cd_pjt,
      nm_column: p.nm_pjt || p.cd_pjt,
      cd_clas: null,
      cd_popup: null,
      dc_editcon: null,
      fg_visible: "Y",
      yn_sum: "Y",
      no_seq: maxSeq + 1 + i,
    }));

    if (!totalCol) return [...staticCols, ...pjtCols];
    return [
      ...staticCols,
      ...pjtCols,
      { ...totalCol, no_seq: maxSeq + 1 + pjtCols.length },
    ];
  };

  // data[0](예산코드 base)에 data[2](예산코드×프로젝트 금액)를 프로젝트 컬럼값으로 펼친다.
  const buildGridRows = (budgetRows: any[], valueRows: any[]) => {
    const rows = (budgetRows ?? []).map((r: any) => ({ ...r }));
    const rowByBudget = new Map<string, any>();
    rows.forEach((r: any) => rowByBudget.set(String(r.cd_budget), r));

    (valueRows ?? []).forEach((v: any) => {
      const row = rowByBudget.get(String(v.cd_budget));
      if (!row) return;
      const key = PJT_PREFIX + v.cd_pjt;
      row[key] = Number(row[key] ?? 0) + Number(v.am_dist ?? 0);
    });
    return rows;
  };
  //#endregion

  const onSearch = async () => {
    try {
      const f = searchRef.current.getFormdata();
      const params = {
        id: "sn_pms_cls_reg_003_01",
        params: {
          ...f,
          ym_start: String(f.dt_from ?? "").substring(0, 6),
          ym_end: String(f.dt_to ?? "").substring(0, 6),
        },
      };

      const data: any = await fnGetDataSet(params);
      const budgetRows = data?.[0] ?? []; // 예산코드 base (합계금액 포함)
      const pjtRows = data?.[1] ?? []; // 프로젝트 목록(동적 컬럼)
      const valueRows = data?.[2] ?? []; // 예산코드×프로젝트 배부금액

      setColums(buildGridColumns(pjtRows));
      setDataSource1(buildGridRows(budgetRows, valueRows));
    } catch (error: any) {}
  };

  //#region 원가집계
  const onAggregate = async () => {
    const f = searchRef.current.getFormdata();
    const cd_biz = f.cd_biz;
    const ym_start = String(f.dt_from ?? "").substring(0, 6);
    const ym_end = String(f.dt_to ?? "").substring(0, 6);

    if (!cd_biz) {
      await showDialog("alter", "사업장을 선택해주세요.");
      return;
    }
    if (ym_start === "" || ym_end === "") {
      await showDialog("alter", "원가기간을 선택해주세요.");
      return;
    }

    // 이미 배부된 자료가 있으면 다시 묻는다. SP 가 해당 종료년월을 지우고 다시 만든다.
    const cntRtn: any = await fnGetDataSet({
      id: "sn_pms_cls_reg_003_05",
      params: { cd_firm: userInfo.cd_firm, cd_biz, ym_end },
    });
    const cnt = cntRtn?.[0] ?? [];
    if (cnt?.[0] && Number(cnt[0].cnt) !== 0) {
      const ok = await showDialog(
        "confirm",
        "이미 처리된 내역이 있습니다. 삭제하고 다시 생성하겠습니까?"
      );
      if (!ok) return;
    }

    const res = await callApi("SN_PMS_CLS_REG_003_Btn", {
      cd_firm: userInfo.cd_firm,
      cd_biz,
      ym_start,
      ym_end,
    });
    // 집계만 하고 화면이 그대로면 됐는지 알 수 없다. 성공했으면 바로 다시 조회한다.
    if (res?.status) {
      await onSearch();
    }
  };
  //#endregion

  /**
   * 원가기간 도움창에서 기간을 고르면 바로 조회한다.
   * source 가 'popup' 일 때만 반응한다 — 직접 입력 중에는 조회하지 않는다.
   */
  const onSearchValueChanged = (event: ValueChangedEvent) => {
    if (event.source !== "popup") return;
    if (event.colId !== "dt_from" && event.colId !== "dt_to") return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      searchTimerRef.current = null;
      onSearch();
    }, 0);
  };

  return (
    <div className="h-full flex flex-col">
      <div>
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchValueChanged}
        ></SearchPanel>
      </div>
      <div className="flex-1 min-h-0 mt-1">
        <SnGrid
          ref={gridRef}
          columnConfig={columns}
          rowData={dataSource1}
          etcProps={{
            rowSelection: "multiple",
            isEditable: false,
            title: "    프로젝트별공통비배부    ", // 그리드 타이틀(좌우 여백: nbsp)
          }}
          headerInfo={headerInfo}
          events={{
            onBtnEtcClick: (sender: string) => {
              if (sender === BTN_AGGREGATE) onAggregate();
            },
          }}
        />
      </div>
    </div>
  );
});

export default React.memo(SN_PMS_CLS_REG_003);
